import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode,
} from 'react'
import { useSucursales } from '@hooks/useSucursales'
import type { SucursalPublic } from '@models/sucursal'

type DomainContextValue = {
    domain: string | null
    sucursal: SucursalPublic | null
    loading: boolean
}

const DomainContext = createContext<DomainContextValue | null>(null)

type DomainProviderProps = {
    children: ReactNode
}

export const DomainProvider = ({ children }: DomainProviderProps) => {
    const [domain, setDomain] = useState<string | null>(null)
    const [sucursal, setSucursal] = useState<SucursalPublic | null>(null)

    const { loading, getSucursalDomainHook } = useSucursales()

    const init = useCallback(async () => {
        const newDomain = window.location.host
        setDomain(newDomain)
        const ret = await getSucursalDomainHook(newDomain)
        if (ret?.data) {
            setSucursal(ret.data)
        }
    }, [getSucursalDomainHook])

    useEffect(() => {
        init()
    }, [init])

    const value = useMemo(
        () => ({
            domain,
            sucursal,
            loading,
        }),
        [domain, sucursal, loading]
    )

    return <DomainContext.Provider value={value}>{children}</DomainContext.Provider>
}

export const useDomainContext = () => {
    const context = useContext(DomainContext)
    if (!context) {
        throw new Error('useDomainContext debe usarse dentro de DomainProvider')
    }
    return context
}
